import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { CortadoIcon } from '@/components/shared/cortado-icon'
import { useAuth } from '@/hooks/useAuth'
import { AppShell } from '@/components/layout/app-shell'
import { ContentHeader } from '@/components/layout/content-header'
import { AIAssistantProvider } from '@/contexts/ai-assistant-context'
import { isDemoModeActive } from '@/lib/demo-mode'

/**
 * 404 content - the actual 404 message and button
 */
function NotFoundContent({ showDashboardButton }: { showDashboardButton: boolean }) {
  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <div className="flex flex-col items-center gap-6 text-center">
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-muted">
          <CortadoIcon className="h-12 w-12" />
        </div>
        <div className="space-y-2">
          <h1 className="text-6xl font-bold tracking-tight">404</h1>
          <h2 className="text-xl font-medium">Page Not Found</h2>
          <p className="text-muted-foreground">
            The page you're looking for doesn't exist or has been moved.
          </p>
        </div>
        <Button asChild>
          {showDashboardButton ? (
            <Link to="/dashboard">Back to Dashboard</Link>
          ) : (
            <Link to="/login">Go to Login</Link>
          )}
        </Button>
      </div>
    </div>
  )
}

/**
 * 404 page for unauthenticated users - full page with branding
 */
function UnauthenticatedNotFoundPage() {
  return (
    <div className="flex min-h-svh w-full flex-col">
      {/* Logo */}
      <div className="absolute left-6 top-6 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center">
          <CortadoIcon className="h-8 w-8" />
        </div>
        <span className="font-semibold">Cortado Pro</span>
      </div>

      <NotFoundContent showDashboardButton={false} />
    </div>
  )
}

/**
 * 404 page for authenticated users - shown within dashboard layout with sidebar
 */
function AuthenticatedNotFoundWithLayout() {
  return (
    <AIAssistantProvider>
      <AppShell>
        <ContentHeader breadcrumbs={[{ label: 'Not Found' }]} />
        <div className="flex-1 overflow-y-scroll">
          <NotFoundContent showDashboardButton={true} />
        </div>
      </AppShell>
    </AIAssistantProvider>
  )
}

/**
 * Smart 404 page that shows the appropriate version based on auth state
 */
export function NotFoundPage() {
  const { session, isLoading } = useAuth()
  const isDemo = isDemoModeActive()

  // While loading auth state, show a simple loading state
  if (isLoading && !isDemo) {
    return <div className="flex min-h-svh items-center justify-center">Loading...</div>
  }

  // If authenticated (or in demo mode), show 404 with dashboard layout
  if (session || isDemo) {
    return <AuthenticatedNotFoundWithLayout />
  }

  // Otherwise show standalone 404
  return <UnauthenticatedNotFoundPage />
}

/**
 * 404 page for authenticated users - shown within dashboard layout (used by _app route)
 */
export function AuthenticatedNotFoundPage() {
  return <NotFoundContent showDashboardButton={true} />
}
